import type { I18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { PERIOD_PRESETS } from './dashboard.period';

// Manifesto do Dashboard (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K.
// No legado a rota é `/` (ou `/dashboard` com `INITIAL_ROUTE`, router.tsx:297-326), com `CreditNote:Read`
// (router.tsx:308-312, Home/index.tsx:13). Aqui fica em `/dashboard`: o `/` é o Início do novo UYZY.

export const DASHBOARD_PATH = '/dashboard';
export const DASHBOARD_READ = can('CreditNote', 'Read');

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra o Dashboard para usar esta ação.`);
};

const civilDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Um botão (`preset`) ou um intervalo (`start` e `end`), nunca os dois. */
export const dashboardPeriodInput = z
  .strictObject({
    preset: z.enum(PERIOD_PRESETS).optional(),
    start: civilDate.optional(),
    end: civilDate.optional(),
  })
  .refine(
    (value) =>
      value.preset === undefined
        ? value.start !== undefined && value.end !== undefined
        : value.start === undefined && value.end === undefined,
    {
      message: 'use preset, or start and end',
    },
  )
  .refine(
    (value) => value.start === undefined || value.end === undefined || value.start <= value.end,
    {
      message: 'start must not be after end',
    },
  );
export type DashboardPeriodInput = z.infer<typeof dashboardPeriodInput>;

// Descrições para o agente em pt-BR com `msg`, servidas no idioma do usuário (U46).
export const dashboardPeriod = action<DashboardPeriodInput>({
  id: 'dashboard.period',
  kind: 'read',
  label: msg`Escolher período`,
  description: msg`Troca o período dos números do Dashboard. Use um botão (today = Hoje, yesterday = Ontem, 1m, 3m, 6m ou 1y = 1 ano) ou um intervalo de datas (AAAA-MM-DD). Os produtos escolhidos continuam.`,
  keywords: [msg`hoje`, msg`ontem`, msg`período`],
  input: dashboardPeriodInput,
  run: mountedScreenRequired,
});

export const dashboardProducts = action({
  id: 'dashboard.products',
  kind: 'draft',
  label: msg`Selecionar produtos`,
  description: msg`Abre a gaveta "Gerenciar produtos" para a pessoa escolher quais produtos o Dashboard considera. A escolha e a confirmação são da pessoa.`,
  keywords: [msg`produtos`],
  run: mountedScreenRequired,
});

export const dashboardRefresh = action({
  id: 'dashboard.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os números do Dashboard, com o período e os produtos atuais.`,
  run: mountedScreenRequired,
});

/** A lista de Operações (`/ccb/operacoes`), que lê `status` da URL como lista (`creditNoteFilters`, tipo `status`). */
export const OPERATIONS_LIST_PATH = '/ccb/operacoes';

/** As etapas do painel que abrem a lista filtrada, com o valor do enum de status do fio. */
/* eslint-disable lingui/no-unlocalized-strings -- valores do enum de status do fio */
export const PIPELINE_STATUSES = [
  'ComplianceApproval',
  'CreditApproval',
  'Signatures',
  'SignaturesValidation',
  'Finished',
] as const;
/* eslint-enable lingui/no-unlocalized-strings */
export type PipelineStatus = (typeof PIPELINE_STATUSES)[number];

export const dashboardOpenOperationsInput = z.strictObject({ status: z.enum(PIPELINE_STATUSES) });
export type DashboardOpenOperationsInput = z.infer<typeof dashboardOpenOperationsInput>;

export const dashboardOpenOperations = action<DashboardOpenOperationsInput>({
  id: 'dashboard.openOperations',
  kind: 'navigate',
  label: msg`Ver operações da etapa`,
  description: msg`Abre, na mesma aba, a lista de Operações filtrada pelo status de uma etapa do Dashboard (ComplianceApproval = Aprovação de compliance, CreditApproval = Aprovação de crédito, Signatures = Coleta de assinaturas, SignaturesValidation = Validação de assinaturas, Finished = Encerrado). A lista abre sem o período do Dashboard. Não muda nada.`,
  keywords: [msg`etapa`, msg`status`, msg`operações`],
  permission: DASHBOARD_READ,
  input: dashboardOpenOperationsInput,
  run: ({ input, nav }) => nav.to(OPERATIONS_LIST_PATH, { search: { status: [input.status] } }),
});

export const dashboardScreen = defineScreen({
  id: 'dashboard.home',
  path: DASHBOARD_PATH,
  title: msg`Dashboard`,
  description: msg`Painel das operações de crédito do correspondente: operações concluídas, valor total operado, operações por etapa, valores por categoria de produto e por mês, no período e nos produtos escolhidos.`,
  permission: DASHBOARD_READ,
  place: { icon: 'layout-dashboard', section: 'dashboard' },
  actions: [dashboardProducts, dashboardRefresh, dashboardPeriod, dashboardOpenOperations],
});

/** O que o agente lê da tela (U24), no idioma do usuário (U46): período, produtos e os totais que aparecem. */
export function dashboardContext(
  input: {
    readonly period: string;
    readonly products: number;
    readonly finished: number | null | undefined;
    readonly inProgress: number | null | undefined;
    readonly categories: number | null | undefined;
    readonly loading: boolean;
  },
  i18n: I18n,
): ScreenContext {
  const period = input.period;
  const products = input.products;
  const scope =
    products > 0 ? i18n._(msg`Produtos escolhidos: ${products}.`) : i18n._(msg`Todos os produtos.`);
  const finished = input.finished ?? 0;
  const progress = input.inProgress ?? 0;
  const numbers = input.loading
    ? i18n._(msg`Carregando.`)
    : i18n._(msg`Operações concluídas: ${finished}. Em andamento: ${progress}.`);
  return {
    summary: `${i18n._(msg`Dashboard de operações de crédito.`)} ${i18n._(msg`Período: ${period}.`)} ${scope} ${numbers}`,
    fields: {
      products: input.products,
      finished: input.finished ?? null,
      inProgress: input.inProgress ?? null,
      categories: input.categories ?? null,
    },
    sensitivity: 'internal',
  };
}
